import React from "react";
import BootstrapCarousel from "react-bootstrap/Carousel";
import useMe from "@/hooks/useMe";
import AddCollectionIcon from "@/components/ui/collection/add-collection-icon";
import ImageCard from "@/components/core/image-card";
import useCollection from "@/hooks/useCollection";
import useSnackbar from "@/hooks/useSnackbar";
import { useMediaQuery } from "react-responsive";

interface WebCarouselProps {}

const WebCarousel: React.FC<WebCarouselProps> = () => {
  const { me, fetchMe } = useMe();
  const { setShowDrawer, saveItem } = useCollection();
  const { showSnackbar } = useSnackbar();
  const [numberPerSlide, setNumberPerSlide] = React.useState(6);
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1300 });

  const collections = React.useMemo(() => {
    return me?.privateInfo?.collections || [];
  }, [me]);

  const slides = React.useMemo(() => {
    const chunks = [];
    for (let i = 0; i < collections.length; i += numberPerSlide) {
      chunks.push(collections.slice(i, i + numberPerSlide));
    }
    const lastIdx = chunks.length - 1;
    if (lastIdx < 0 || chunks[lastIdx].length === numberPerSlide) {
      chunks.push(["ADD_COLLECTION"]);
    } else {
      chunks[lastIdx].push("ADD_COLLECTION");
    }
    return chunks;
  }, [collections, numberPerSlide]);

  const collectionCoverImages = (collection: any) => {
    return collection.items.slice(0, 4).map((item: any) => item.thumbnailImage);
  };

  const handleSave = async (collection: any) => {
    if (!collection.id) {
      return;
    }
    saveItem({
      collection,
      onSuccess: () => {
        showSnackbar(`Item added to "${collection.name}"`);
        setShowDrawer(false);
        fetchMe();
      },
    });
  };

  const onCreateCollectionSuccess = (collection: any) => {
    handleSave(collection);
  };

  React.useEffect(() => {
    if (isTablet) {
      setNumberPerSlide(5);
    } else {
      setNumberPerSlide(6);
    }
  }, [isTablet]);

  if (collections.length === 0) {
    return <></>;
  }

  return (
    <>
      <BootstrapCarousel
        interval={null}
        indicators={false}
        prevIcon={
          <div className="carousel-arrow">
            <img src="/back.svg" alt="previous" />
          </div>
        }
        nextIcon={
          <div className="carousel-arrow right-arrow">
            <img src="/back.svg" alt="next" />
          </div>
        }
      >
        {slides.map((slide, index) => {
          return (
            <BootstrapCarousel.Item key={index}>
              <div className="web-carousel-slide">
                {slide.map((collection: any) => {
                  return collection !== "ADD_COLLECTION" ? (
                    <div
                      key={collection.id}
                      className="web-carousel-collection"
                    >
                      <div
                        className="collection-image"
                        style={{
                          cursor: !collection.id ? "default" : "pointer",
                        }}
                        onClick={() => handleSave(collection)}
                      >
                        <ImageCard images={collectionCoverImages(collection)} />
                      </div>
                      <div className="collection-info">
                        <div className="collection-name">{collection.name}</div>
                        <div className="collection-items-number">
                          {collection.items.length} items
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="web-carousel-collection">
                      <AddCollectionIcon
                        style={{ aspectRatio: 1 }}
                        // @ts-expect-error ignore
                        onSuccess={onCreateCollectionSuccess}
                      />
                      <div className="collection-info">
                        <div className="collection-name create-collection">
                          Create new collection
                        </div>
                        <div className="collection-items-number">&#20;</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </BootstrapCarousel.Item>
          );
        })}
      </BootstrapCarousel>
      <style jsx>{`
        .web-carousel-slide {
          display: flex;
          align-items: start;

          .web-carousel-collection {
            --collection-width: 176px;
            --number-per-slide: 6;
            display: flex;
            flex-direction: column;
            width: var(--collection-width);
            flex-shrink: 0;
            margin-right: calc(
              calc(
                  100% - calc(var(--number-per-slide) * var(--collection-width))
                ) / calc(var(--number-per-slide) - 1)
            );

            &:last-child {
              margin-right: 0;
            }

            .collection-image {
              flex: auto;
              object-fit: cover;
              object-position: center bottom;
              aspect-ratio: 1;
              cursor: pointer;
            }

            .collection-info {
              height: 73px;
            }

            .collection-name {
              color: #191919;
              font-size: 16px;
              font-style: normal;
              font-weight: 500;
              line-height: normal;
              letter-spacing: -0.32px;
              margin-top: 16px;
              text-align: left;
              max-height: 57px;
              overflow: hidden;
              text-overflow: ellipsis;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
              font-smooth: never;

              &.create-collection {
                color: #999;
              }
            }

            .collection-items-number {
              color: #666;
              font-size: 14px;
              font-style: normal;
              font-weight: 500;
              line-height: normal;
              letter-spacing: -0.28px;
              margin-top: 4px;
            }
          }
        }

        .carousel-arrow {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #e7e7e7;
          background: #fff;
          &.right-arrow > img {
            transform: rotate(180deg);
          }
        }

        @media (max-width: 1300px) {
          .web-carousel-slide {
            .web-carousel-collection {
              --collection-width: 182px;
              --number-per-slide: 5;
            }
          }
        }

        @media (max-width: 1152px) {
          .web-carousel-slide {
            .web-carousel-collection {
              --collection-width: 134px;
            }
          }
        }
      `}</style>
    </>
  );
};

export default WebCarousel;
